import 'gleam-shared/embeds/WidgetPreview.scss'

import debounce from 'gleam-shared/helpers/debounce'
import extractKey from 'gleam-shared/domain/extractKey'
import updateQuery from 'gleam-shared/network/updateQuery'
import onceDetaches from 'gleam-shared/browser/onceDetaches'
import extractOrigin from 'gleam-shared/network/extractOrigin'

import { writeDom, cancelFastDom } from 'gleam-shared/browser/fastDom'

import DeadPreview from 'gleam-shared/embeds/DeadPreview'
import BasePreview from 'gleam-shared/embeds/BasePreview'

const PLACEHOLDER = 'widget-preview__placeholder'
const PLACEHOLDER_TILES = [
  'widget-preview__skeleton widget-preview__skeleton--title',
  'widget-preview__skeleton widget-preview__skeleton--image',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action',
  'widget-preview__skeleton widget-preview__skeleton--action'
]

export const HEIGHT_TYPE = 'EmbedHeight'
export const STYLES_TYPE = 'EmbedStyles'
const MESSAGE_RE = RegExp(`(${HEIGHT_TYPE}|${STYLES_TYPE}).{5}=(.+)`)

const DEBOUNCE = 300
const RESIZE_MESSAGE = 'resizePopup'

export function injectPlaceholder(parent) {
  preconfigure(parent)
}

export default function WidgetPreview(parent, url, leaderboard) {
  try {
    const frameSrc = buildFrameSrc(url, leaderboard)

    return RealPreview(parent, frameSrc)
  } catch (err) {
    return DeadPreview('The widget preview', err)
  }
}

function RealPreview(parent, frameSrc) {
  const preview = BasePreview({
    destroy,
    description: `The ${extractKey(frameSrc)} widget`
  })

  let frameShown = false
  let frameHeight = null
  let frameOptions = null

  const frame = buildFrame()
  const debouncedShow = debounce(lazyShow, DEBOUNCE)
  const detachObserver = onceDetaches(parent, destroy)

  window.addEventListener('message', onMessage)
  writeDom(configure)

  return preview

  function configure() {
    preconfigure(parent)
    parent.append(frame)
    setFrameSrc(frame, frameSrc)
  }

  function destroy() {
    debouncedShow.cancel()
    detachObserver.destroy()
    cancelFastDom(show)
    cancelFastDom(configure)
    cancelFastDom(applyStyles)
    cancelFastDom(applyHeight)
    window.removeEventListener('message', onMessage)
  }

  function onMessage({ source, data }) {
    if (source !== frame.contentWindow) {
      return
    }

    const { type, payload } = parseMessage(data)

    if (type === STYLES_TYPE) {
      style(payload)
    } else if (type === HEIGHT_TYPE) {
      resize(payload)
    }
  }

  function style(payload) {
    frameOptions = payload.designOptions
    writeDom(applyHeight)
    writeDom(applyStyles)
    tryShowing()
    triggerResize()
  }

  function triggerResize() {
    frame.contentWindow.postMessage(RESIZE_MESSAGE, '*')
  }

  function applyStyles() {
    for (const [key, value] of Object.entries(frameOptions)) {
      frame.style.setProperty(key, value)
    }
  }

  function resize(payload) {
    frameHeight = payload
    writeDom(applyHeight)
    tryShowing()
  }

  function applyHeight() {
    if (frameShown) {
      parent.style.setProperty('height', frameHeight + 'px')
      frame.style.setProperty('height', '100%')
    } else {
      parent.style.removeProperty('height')
      frame.style.setProperty('height', frameHeight + 'px')
    }
  }

  function tryShowing() {
    if (!frameShown && frameOptions !== null && frameHeight !== null) {
      debouncedShow()
    }
  }

  function lazyShow() {
    writeDom(show)
  }

  function show() {
    if (frameShown) {
      return
    }

    frameShown = true
    applyHeight()
    frame.classList.add('widget-preview__frame--loaded')

    const placeholder = findPlaceholder(parent)
    if (placeholder) {
      placeholder.classList.add('widget-preview__placeholder--loaded')
    }

    preview.loadEmitter.emit()
  }
}

function parseMessage(data) {
  try {
    const match = MESSAGE_RE.exec(data)
    const type = match[1]
    const payload = JSON.parse(match[2])
    return { type, payload }
  } catch {
    return {}
  }
}

function findPlaceholder(parent) {
  return parent.getElementsByClassName(PLACEHOLDER)[0]
}

function preconfigure(parent) {
  parent.classList.add('widget-preview__parent')

  const foundPlaceholder = findPlaceholder(parent)

  if (foundPlaceholder) {
    return
  }

  const placeholder = buildElement('section', PLACEHOLDER)

  for (const tileClassName of PLACEHOLDER_TILES) {
    const tile = buildElement('section', tileClassName)
    placeholder.append(tile)
  }

  parent.append(placeholder)
}

function buildFrame() {
  const frame = buildElement('iframe', 'widget-preview__frame')
  frame.setAttribute('allow', 'storage-access *; attribution-reporting *; autoplay *; fullscreen *')
  frame.setAttribute('scrolling', 'no')
  return frame
}

function buildFrameSrc(url, leaderboard) {
  // It would be ideal to match the origin to the widget domain,
  // i.e. https://gleam.io to https://widget.gleamjs.io,
  // but for embedding widgets on the same domain, it's unnecessary

  const key = extractKey(url)
  const origin = extractOrigin(url)

  if (!key || !origin) {
    throw new Error('The iframe SRC couldn\'t be built!')
  }

  if (leaderboard) {
    return `${origin}/${key}/leaderboard`
  } else {
    return buildEmbedSrc(origin, key)
  }
}

function buildEmbedSrc(origin, key) {
  const params = new URLSearchParams(window.location.search)

  return updateQuery(`${origin}/${key}/embed`, {
    l: prepareParam(location.href),
    r: prepareParam(document.referrer),
    gsr: prepareParam(params.get('gsr'))
  })
}

function prepareParam(param) {
  if (!param) {
    return
  }

  return encodeURIComponent(param.slice(0, 1024))
}

function setFrameSrc(frame, frameSrc) {
  frame.src = frameSrc
  return frame
}

function buildElement(tagName, className) {
  const element = document.createElement(tagName)
  element.className = className
  return element
}
